import { createContext, useState, useContext, useEffect, useRef } from "react"

import { loadPyodide } from "pyodide"

import { AuthContext } from "../../../context/AuthContext"
import { SessionContext } from "../../../context/SessionContext"
import { TelemetryContext } from "./TelemetryContext"

import { saveRunToDB } from "../utils/TerminalOutput/submissionUtils"
import { executeCode, parseTestResults, buildSource } from "../service/judge0Service"
import { getSessionLanguage, isCompiledSession } from "../service/languageConfig"
import { getGuiSessionConfig } from '../config/guiSessionConfig'
import { evaluateWebDevChecks } from '../utils/webDevChecks'
import { testsEnabled, isPythonFamily } from '../../session/sessionSettings'
import { isWorkspaceStudySession } from '../../session/studentView/studentSurface'
import { freshMainGlobals } from '../../session/pyodideMain'
import {
  codeHash,
  completeTestResults,
  failingTestLabels,
  hiddenSafePythonRun,
  newRunId,
  providedCodeOf,
  pythonErrorName,
  pythonHarnessStarted,
  readPythonDetails,
  readPythonResult,
  testLabel,
  testOutcomes as outcomesOf,
  studentLineNumbers,
  upgradeLegacyPythonHarness,
  withProvidedCode,
} from '../../session/testResults'
import { isStudySession } from '../study/studyConfig'
import { PRINT_NOTE, isHiddenOutputNotice } from '../study/printNote'
import { addCodeAndUpdateUser } from "../../../service/codeService"
import { createSubmission, updateSubmission, createErr } from "../../../service/submissionService"
import { getPassrateByResult } from "../../../tool/progressUnit"

import { TERMINAL_EVENTS } from '../constants/telemetryEvents'

export const TerminalContext = createContext()

// Helper to extract Python error type from error message
const getErrorType = (errorMessage) => {
  const match = errorMessage.match(/^(\w+Error)/)
  return match ? match[1] : 'UnknownError'
}

export const TerminalProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext)
  const { session } = useContext(SessionContext) 
  
  const { logTelemetryEvent } = useContext(TelemetryContext)

  const [terminalOutput, setTerminalOutput] = useState([])
  const [testResults, setTestResults] = useState([])
  // How each test of the latest run ended (testResults.testOutcomes), next to testResults'
  // pass/fail: [{ status, errorType, message }].
  const [testOutcomes, setTestOutcomes] = useState([])
  const [isRunning, setIsRunning] = useState(false)
  // A study session's latest finished run, for the tutor: { runId, codeHash, failingTests,
  // errorName, at }. Stays null in regular sessions.
  const [lastRunInfo, setLastRunInfo] = useState(null)

  const pyodideRef = useRef(null)
  const pyodideInitPromise = useRef(null)
  // Pending code/submission saves, in run order (see saveSubmissionRecords).
  const saveChainRef = useRef(Promise.resolve())

  const language = getSessionLanguage(session?.id)
  const isCompiled = isCompiledSession(session?.id)
  const guiConfig = getGuiSessionConfig(session)

  // Initialize Pyodide on mount (only for Python sessions)
  useEffect(() => {
    if (isCompiled || guiConfig) return

    const initPyodide = async () => {
      try {
        const pyodide = await loadPyodide({
          indexURL: "https://cdn.jsdelivr.net/pyodide/v0.23.4/full/",
        })
        // See VizmentalIDE.jsx's runTests for the same fix - numpy isn't
        // available until explicitly loaded.
        await pyodide.loadPackage('numpy')
        pyodideRef.current = pyodide
      } catch (err) {
        console.error("Failed to load Pyodide:", err)
      }
    }
    
    pyodideInitPromise.current = initPyodide()
  }, [isCompiled, guiConfig])

  // Every run's per-test results reach the panel through here. runRecord (study sessions,
  // see runTests) also keeps them, for lastRunInfo.
  // outcomes: testOutcomes for the run; runs that cannot tell (compiled, web) say passed or
  // failed from the results.
  const showTestResults = (results, runRecord, outcomes = null) => {
    if (runRecord) runRecord.results = results
    setTestResults(results)
    setTestOutcomes(outcomes || outcomesOf(results, [], true))
  }

  // Shows one run's per-test results and logs them: the terminal's test_result line and
  // RESULT telemetry. Callers pad the array to the full test list first (testResults.js),
  // so the pass rate is over every test, not just the ones that reported.
  const reportTestResults = (executionResults, addLog, runRecord = null, outcomes = null) => {
    const ended = outcomes || outcomesOf(executionResults, [], true)
    showTestResults(executionResults, runRecord, ended)
    const passed = executionResults.filter(r => r === true).length
    const passrate = executionResults.length > 0
      ? (passed / executionResults.length) * 100
      : 0

    const detailedResults = executionResults.map((result, index) => ({
      test_case_number: index + 1,
      passed: result === true,
      test_name: `Test Case ${index + 1}`,
      // How the test ended: passed, failed, assertion_failed, error or not_run (a run that
      // stopped before the tests); `passed` stays as existing readers expect.
      status: ended[index]?.status || (result === true ? 'passed' : 'failed'),
      // Study runs also name the test (its testName), and say what a test raised; test_name
      // stays the generic label existing readers expect. A message only for a visible test.
      ...(runRecord && {
        test_label: testLabel(session?.test_list, index),
        ...(ended[index]?.errorType && { error_type: ended[index].errorType }),
        ...(ended[index]?.message && { message: ended[index].message }),
      })
    }))

    addLog('test_result', `Tests Completed: ${passrate}%`, {
      test_results: executionResults,
      detailed_results: detailedResults,
      passrate: passrate
    })

    logTelemetryEvent(TERMINAL_EVENTS.RESULT, {
      passrate: passrate,
      passed_count: passed,
      total_tests: executionResults.length,
      detailed_results: detailedResults,
      ...runRecord?.fields
    })
  }

  // /session's VizmentalIDE saves a code row and the student's submission after every run,
  // and the instructor dashboards (Live Feed, pass-rate table, Groups, header pass rates)
  // read those rather than terminal_output - so the Python family saves them here too, with
  // the same values. Chained so two quick runs cannot land out of order, and never awaited
  // by the run: a failed or slow save must not hold up or break the workspace.
  const saveSubmissionRecords = (userCode, executionResults, errorText) => {
    if (!currentUser || !session) return
    const userId = currentUser.id
    const sessionId = session.id
    const passrate = getPassrateByResult(executionResults)
    saveChainRef.current = saveChainRef.current
      .then(async () => {
        // /workspace has no keystroke log (that lives in /session's SelectedCodeContext).
        const saved = await addCodeAndUpdateUser(userCode, passrate, userId, [])
        if (!saved?.success) return
        await updateSubmission(createSubmission(
          userId,
          saved.code.id,
          sessionId,
          executionResults,
          passrate,
          createErr(errorText)
        ))
      })
      .catch((error) => console.error("Failed to save code and submission:", error))
  }

  // ── Compiled Language Execution via Judge0 (C, Java) ────────
  const runTestsCompiled = async (userCode, runRecord = null) => {
    let currentRunLogs = []

    const addLog = (type, content, extra = {}) => {
      const entry = { type, content, ...extra }
      currentRunLogs.push(entry)
      setTerminalOutput((prev) => [...prev, entry])
    }

    // Tests off: compile the student's code alone - no hidden harness, so no RESULT:
    // lines and no pass rate.
    const hasTests = testsEnabled(session)
    // Tests on: every test ends as pass or fail, whatever the run did - RESULT lines
    // missing after a compile error, crash or runner failure count as failed.
    const completeResults = (stdout) =>
      completeTestResults(parseTestResults(stdout || ''), session?.test_list?.length)

    try {
      const hiddenCode = hasTests ? session?.test_code?.unit_test || "" : ""
      const starterCode = hasTests ? session?.test_code?.starter || "" : ""
      const source = buildSource(starterCode, userCode, hiddenCode)

      addLog('stdout', 'Compiling and running...')

      const result = await executeCode(source, language)

      // Handle compilation or runtime error
      if (!result.success) {
        addLog('stderr', result.error.message, {
          error: { message: result.error.message, type: result.error.type }
        })

        logTelemetryEvent(TERMINAL_EVENTS.ERROR, {
          message: result.error.message,
          type: result.error.type,
          ...runRecord?.fields
        })
        // Compiled runs have no traceback; the runner's error type is their error's name.
        if (runRecord) runRecord.errorName = result.error.type || null
        // A runtime error can come after some tests already printed their RESULT line.
        if (hasTests) showTestResults(completeResults(result.stdout), runRecord)
        return currentRunLogs
      }

      // Log any stdout lines (print statements from student code)
      if (result.stdout) {
        const outputLines = result.stdout.split('\n')
        for (const line of outputLines) {
          if (!line.startsWith('RESULT:') && line.trim()) {
            addLog('stdout', line)
            logTelemetryEvent(TERMINAL_EVENTS.OUTPUT, { output: line, ...runRecord?.fields })
          }
        }
      }

      // Parse RESULT lines into one boolean per test
      if (hasTests) {
        const executionResults = completeResults(result.stdout)
        if (executionResults.length > 0) reportTestResults(executionResults, addLog, runRecord)
      }

    } catch (err) {
      const errorMessage = err.message || err.toString()
      addLog('stderr', errorMessage, {
        error: { message: errorMessage, type: 'Judge0Error' }
      })

      logTelemetryEvent(TERMINAL_EVENTS.ERROR, {
        message: errorMessage,
        type: 'Judge0Error',
        ...runRecord?.fields
      })
      if (runRecord) runRecord.errorName = 'Judge0Error'
      if (hasTests) showTestResults(completeResults(''), runRecord)
    }

    return currentRunLogs
  }

  // ── Python Execution via Pyodide ────────────────────────────
  const runTestsPython = async (userCode, runRecord = null) => {
    let currentRunLogs = []

    // Updates both UI (state) and DB Data (array)
    const addLog = (type, content, extra = {}) => {
      const entry = { type, content, ...extra }
      currentRunLogs.push(entry)
      setTerminalOutput((prev) => [...prev, entry])
    }

    // Tests off: run only the student's code, and never read a `result` global back as
    // test results (a student's own top-level `result` list would otherwise be scored).
    const hasTests = testsEnabled(session)
    // This run's own namespace (see below); stays null if the engine never loaded.
    let run = null
    let engineLoaded = false
    let errorText = ''
    // The lines of the run's source before the student's code (studentLineNumbers).
    let linesBefore = 0
    // Whether the harness said a hidden test's call printed something (study/printNote.js).
    let hiddenOutput = false

    try {
      // If engine is missing, wait for it to load
      if (!pyodideRef.current) {
        await pyodideInitPromise.current
      }
      // Double check it is loaded now
      if (!pyodideRef.current) throw new Error("Python engine failed to load.")
      
      const pyodide = pyodideRef.current
      engineLoaded = true
      // Harnesses saved before per-test isolation are upgraded here, without recompiling.
      const hiddenCode = hasTests ? upgradeLegacyPythonHarness(session?.test_code?.unit_test || "") : ""
      // The exercise's provided code (E3's Person) runs before the student's code, with or
      // without tests (testResults.withProvidedCode).
      const starterCode = withProvidedCode(hasTests ? session?.test_code?.starter || "" : "", providedCodeOf(session?.test_code))
      
      pyodide.setStdout({
        batched: (msg) => {
          if (isHiddenOutputNotice(msg)) hiddenOutput = true
          addLog('stdout', msg)
          logTelemetryEvent(TERMINAL_EVENTS.OUTPUT, { output: msg, ...runRecord?.fields })
        },
      })

      // The engine is shared across runs, so each run gets a fresh __main__ module
      // (pyodideMain.js): a function the student deleted or renamed, or an earlier run's
      // `result`, cannot carry over and pass, while `if __name__ == "__main__":`, pickle and
      // doctest still find the student's code.
      run = freshMainGlobals(pyodide)

      // Hidden tests stay out of the student's code's reach (no test-data globals, no output
      // while they run) wherever the setup and harness are the generated ones; hand-written
      // ones run as saved (testResults.hiddenSafePythonRun). In a study session each check of
      // a class exercise also starts from a fresh run of the student's code.
      const safeRun = hasTests
        ? hiddenSafePythonRun({
            starterCode,
            unitTest: session?.test_code?.unit_test || "",
            testList: session?.test_list,
            freshStatePerCheck: isStudySession(session),
          })
        : null
      const combinedCode = safeRun ? safeRun.source(userCode) : `${starterCode}\n${userCode}\n${hiddenCode}`
      linesBefore = safeRun ? safeRun.linesBefore : starterCode.split('\n').length
      await pyodide.runPythonAsync(combinedCode, { globals: run.globals })

    } catch (err) {
      const errorMessage = err.message || err.toString()
      // What the student (and the study tutor, through the terminal) sees: the editor's line
      // numbers. The telemetry keeps the raw text too, comparable with earlier runs.
      const shownMessage = studentLineNumbers(errorMessage, linesBefore, String(userCode).split('\n').length)
      const errorType = getErrorType(errorMessage)
      errorText = err.toString()
      // Study runs also record the exception's own name. errorType reads the message's first
      // word, and a Python traceback starts with "Traceback", so it is UnknownError for every
      // Python error. Pyodide's PythonError carries the class name too, if the text has none.
      const errorName = runRecord
        ? pythonErrorName(errorMessage) ?? (typeof err.type === 'string' ? err.type : null)
        : null
      if (runRecord) runRecord.errorName = errorName
      const errorNameField = runRecord ? { error_name: errorName } : null

      addLog('stderr', shownMessage, {
        error: { message: shownMessage, type: errorType, stack: err.stack, ...errorNameField }
      })

      logTelemetryEvent(TERMINAL_EVENTS.ERROR, {
        message: errorMessage,
        ...(shownMessage !== errorMessage ? { display_message: shownMessage } : {}),
        type: errorType,
        ...runRecord?.fields,
        ...errorNameField
      })
    }

    // Tests on: one pass or fail per test after every run - a syntax error, an uncaught
    // exception or a missing engine leaves tests without a result, and those count as
    // failed (testResults.js).
    let executionResults = []
    if (hasTests) {
      executionResults = completeTestResults(run ? readPythonResult(run.globals) : [], session?.test_list?.length)
      // A run that stopped before the harness (syntax error, top-level exception, no engine)
      // leaves every test not run, which its results alone show as failed.
      const started = run ? pythonHarnessStarted(run.globals) : false
      const outcomes = outcomesOf(executionResults, started ? readPythonDetails(run.globals) : [], started)
      if (executionResults.length > 0) {
        reportTestResults(executionResults, addLog, runRecord, outcomes)
      } else {
        showTestResults(executionResults, runRecord, outcomes)
      }
    }

    // A study condition: the harness said a hidden test's call printed something it does not
    // show - say how to see one's own output (study/printNote.js), and log that it was shown.
    if (isStudySession(session) && hiddenOutput) {
      addLog('note', PRINT_NOTE)
      logTelemetryEvent(TERMINAL_EVENTS.NOTE, { note: 'print-in-method', ...runRecord?.fields })
    }

    try {
      run?.release()
    } catch (releaseError) {
      // already released
    }

    // VizmentalIDE saves nothing when the engine itself failed to load, so neither does this.
    // Study sessions keep exactly the data streams their studies were designed around.
    // Only students' runs: an instructor trying the page from Monitor's "Open Workspace"
    // must not show up in the class's pass rates (VizmentalIDE was student-only too).
    const isStudentRun = currentUser?.role === 3 || currentUser?.role === 4
    if (engineLoaded && isStudentRun && isPythonFamily(session?.type) && !isWorkspaceStudySession(session)) {
      saveSubmissionRecords(userCode, executionResults, errorText)
    }

    return currentRunLogs
  }

  // ── Web Dev HTML/CSS/JS Validation ──────────────────────────
  // The checks themselves live in utils/webDevChecks.js (pure, shared with the creation
  // wizard's live student preview); this replays their logs, results and telemetry.
  const runTestsWebDev = async (userCode, runRecord = null) => {
    let currentRunLogs = []

    const addLog = (type, content, extra = {}) => {
      const entry = { type, content, ...extra }
      currentRunLogs.push(entry)
      setTerminalOutput((prev) => [...prev, entry])
    }

    const { logs, results, telemetry } = evaluateWebDevChecks(guiConfig, userCode)
    logs.forEach((log) => addLog(log.type, log.content))
    if (results.length) showTestResults(results, runRecord)
    if (telemetry) {
      logTelemetryEvent(
        TERMINAL_EVENTS[telemetry.event],
        runRecord ? { ...telemetry.payload, ...runRecord.fields } : telemetry.payload
      )
    }

    return currentRunLogs
  }

  // ── Main Entry Point ────────────────────────────────────────
  const runTests = async (userCode) => {
    if (isRunning) return

    setIsRunning(true)
    setTerminalOutput([])

    const isYCHFall2026 = Boolean(guiConfig)

    // In a study session this click gets a run id and the hash of the code it runs, and both
    // go on every record the run writes (its telemetry and its terminal_output row), so each
    // output, error and test result ties to the exact code version. The runners fill in
    // `results` and `errorName` for lastRunInfo. Regular sessions log what they always have.
    const runRecord = isStudySession(session)
      ? { fields: { run_id: newRunId(), code_hash: codeHash(userCode) }, results: [], errorName: null }
      : null

    logTelemetryEvent(TERMINAL_EVENTS.RUN, {
      code_length: userCode.length,
      code: userCode,
      language: isYCHFall2026 ? 'webdev' : language,
      timestamp: Date.now(),
      ...runRecord?.fields
    })

    let currentRunLogs

    try {
      if (isYCHFall2026) {
        currentRunLogs = await runTestsWebDev(userCode, runRecord)
      } else if (isCompiled) {
        currentRunLogs = await runTestsCompiled(userCode, runRecord)
      } else {
        currentRunLogs = await runTestsPython(userCode, runRecord)
      }
    } finally {
      setIsRunning(false)
      saveRunToDB(
        currentRunLogs || [],
        currentUser,
        session,
        userCode,
        runRecord?.fields
      )
      if (runRecord) {
        setLastRunInfo({
          runId: runRecord.fields.run_id,
          codeHash: runRecord.fields.code_hash,
          failingTests: failingTestLabels(runRecord.results, session?.test_list),
          errorName: runRecord.errorName,
          at: Date.now(),
        })
      }
    }
  }

  return (
    <TerminalContext.Provider value={{
      terminalOutput,
      testResults,
      testOutcomes,
      isRunning,
      runTests,
      lastRunInfo
    }}>
      {children}
    </TerminalContext.Provider>
  )
}
